import MarkdownIt from "markdown-it";

// Configure markdown-it with security-focused options
const md = new MarkdownIt({
  html: false, // Disable HTML tags for security
  xhtmlOut: true, // Use XHTML-style self-closing tags
  breaks: true, // Convert line breaks to <br>
  linkify: true, // Auto-convert URLs to links
  typographer: true, // Enable smart quotes and other typographic replacements
});

// Configure link security
md.renderer.rules.link_open = function (tokens, idx, options, env, renderer) {
  // Add target="_blank" and rel="noopener noreferrer" to all links for security
  const aIndex = tokens[idx].attrIndex("target");
  const relIndex = tokens[idx].attrIndex("rel");

  if (aIndex < 0) {
    tokens[idx].attrPush(["target", "_blank"]);
  } else if (tokens[idx].attrs) {
    tokens[idx].attrs[aIndex][1] = "_blank";
  }

  if (relIndex < 0) {
    tokens[idx].attrPush(["rel", "noopener noreferrer"]);
  } else if (tokens[idx].attrs) {
    tokens[idx].attrs[relIndex][1] = "noopener noreferrer";
  }

  return renderer.renderToken(tokens, idx, options);
};

/**
 * Parse markdown text and return safe HTML
 * @param text - The markdown text to parse
 * @returns Safe HTML string
 */
export const parseMarkdown = (text: string): string => {
  if (!text || typeof text !== "string") {
    return "";
  }

  try {
    return md.render(text);
  } catch (error) {
    console.error("Error parsing markdown:", error);
    // Fallback to plain text if parsing fails
    return text.replace(/\n/g, "<br>");
  }
};

/**
 * Parse markdown text inline (without block elements)
 * @param text - The markdown text to parse inline
 * @returns Safe HTML string
 */
export const parseMarkdownInline = (text: string): string => {
  if (!text || typeof text !== "string") {
    return "";
  }

  try {
    return md.renderInline(text);
  } catch (error) {
    console.error("Error parsing inline markdown:", error);
    // Fallback to plain text if parsing fails
    return text;
  }
};

/**
 * Strip markdown formatting and return plain text
 * @param text - The markdown text to strip
 * @returns Plain text string without markdown formatting
 */
export const stripMarkdown = (text: string): string => {
  if (!text || typeof text !== "string") {
    return "";
  }

  try {
    // Parse markdown to HTML first
    const html = md.render(text);

    // Create a temporary DOM element to extract text content
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Extract plain text content
    return tempDiv.textContent || tempDiv.innerText || "";
  } catch (error) {
    console.error("Error stripping markdown:", error);
    // Fallback: Simple regex-based markdown stripping
    return text
      .replace(/\*\*(.*?)\*\*/g, "$1") // Bold
      .replace(/\*(.*?)\*/g, "$1") // Italic
      .replace(/__(.*?)__/g, "$1") // Bold (underscore)
      .replace(/_(.*?)_/g, "$1") // Italic (underscore)
      .replace(/`(.*?)`/g, "$1") // Inline code
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Links
      .replace(/#+\s*(.*)/g, "$1") // Headers
      .replace(/>\s*(.*)/g, "$1") // Blockquotes
      .replace(/[-*+]\s*/g, "") // List bullets
      .replace(/\d+\.\s*/g, "") // Numbered lists
      .trim();
  }
};
